import { FormEvent, useState } from 'react';
import { Plus, Trash2, Clock } from 'lucide-react';
import { CalendarProfessional, ServiceType } from '../../types/calendar';
import { Form, FormField, FormLabel, FormInput, FormTextarea, Button } from '../ui';

interface ProfessionalFormProps {
  professional: CalendarProfessional | null;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  onClose: () => void;
}

function ProfessionalForm({ professional, onSubmit, isLoading, onClose }: ProfessionalFormProps) {
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>(
    professional?.service_types || []
  );

  const addServiceType = () => {
    setServiceTypes([...serviceTypes, { id: 0, name: '', duration_minutes: 30 }]);
  };

  const removeServiceType = (index: number) => {
    setServiceTypes(serviceTypes.filter((_, i) => i !== index));
  };

  const updateServiceType = (index: number, field: keyof ServiceType, value: string | number) => {
    const updatedServiceTypes = [...serviceTypes];
    updatedServiceTypes[index] = {
      ...updatedServiceTypes[index],
      [field]: value,
    };
    setServiceTypes(updatedServiceTypes);
  };

  return (
    <Form onSubmit={onSubmit}>
      <FormField>
        <FormLabel htmlFor="name" required>
          Nome
        </FormLabel>
        <FormInput id="name" name="name" defaultValue={professional?.name} required />
      </FormField>

      <FormField>
        <FormLabel htmlFor="specialty">Especialidade</FormLabel>
        <FormTextarea
          id="specialty"
          name="specialty"
          defaultValue={professional?.specialty}
          rows={3}
        />
      </FormField>

      <div className="mt-6 mb-4">
        <div className="flex justify-between items-center mb-2">
          <FormLabel className="text-lg">Tipos de Serviço</FormLabel>
          <Button type="button" variant="secondary" size="sm" onClick={addServiceType}>
            <Plus className="h-4 w-4 mr-1" /> Adicionar Serviço
          </Button>
        </div>

        {serviceTypes.length === 0 ? (
          <div className="text-center py-4 text-gray-500 border border-dashed rounded-md">
            Nenhum tipo de serviço adicionado
          </div>
        ) : (
          <div className="space-y-3">
            {serviceTypes.map((serviceType, index) => (
              <div
                key={index}
                className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-md"
              >
                {/* Hidden input for service type ID */}
                <input
                  type="hidden"
                  name={`service_types[${index}].id`}
                  value={serviceType.id || 0}
                />

                <div className="flex-1">
                  <FormField>
                    <FormLabel htmlFor={`service_types[${index}].name`} required>
                      Nome do Serviço
                    </FormLabel>
                    <FormInput
                      id={`service_types[${index}].name`}
                      name={`service_types[${index}].name`}
                      value={serviceType.name}
                      onChange={e => updateServiceType(index, 'name', e.target.value)}
                      required
                    />
                  </FormField>
                </div>
                <div className="w-32">
                  <FormField>
                    <FormLabel htmlFor={`service_types[${index}].duration_minutes`} required>
                      Duração (min)
                    </FormLabel>
                    <FormInput
                      id={`service_types[${index}].duration_minutes`}
                      name={`service_types[${index}].duration_minutes`}
                      type="number"
                      min="5"
                      step="5"
                      value={serviceType.duration_minutes}
                      onChange={e =>
                        updateServiceType(index, 'duration_minutes', parseInt(e.target.value))
                      }
                      required
                    />
                  </FormField>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-8"
                  onClick={() => removeServiceType(index)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {professional ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default ProfessionalForm;
